import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Parser } from "html-to-react";

// Custom hooks
import { useListTableState } from "src/hooks/useListTableState";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	Icon,
	IconButton,
	Pagination,
	Switch,
	Table,
	Text,
	MetreaTooltip,
} from "@metrea-ui/v2";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import styles from "./UserGroupsList.module.css";
import { EnhancedTableHead, EnhancedTableToolbar } from "src/components/UI/common/EnhancedTableParts";
import CardList from "src/components/UI/CardList/CardList";
import { ChevronLeftRegular, ChevronRightRegular, InfoRegular } from "@fluentui/react-icons";

// Utilities
import { isDevTeam, isDeveloper } from "src/utils/sessionUtils";
import { debugLog, isDevSiteNow, showTesting } from "src/utils/debugUtils";
import { getShortDateString, getShortDateTimeString, highlight } from "src/utils/formatUtils";

const SEARCH_FIELDS = ["name", "group_code", "member_desc"];

const UserGroupsList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;
	const { onSelectUserGroup } = props;

	const htmlParser = new Parser();

	// --- Redux Selectors ---
	const userGroupsList = useSelector((state) => state.userGroups?.list ?? []);

	// --- Local State ---
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState([]);
	const [searchKeyword, setSearchKeyword] = useState("");

	useEffect(() => {
		debugLog(2, "[UserGroupsList][FLOW] mounted", {});
	}, []);

	// Helper to return filtered Groups based on Active/Inactive toggle.
	const visibleGroups = useMemo(() => {
		const activeFiltered = showInactive
			? userGroupsList
			: userGroupsList.filter((x) => x.status_id === 1);
		if (!searchKeyword) return activeFiltered;

		const kw = searchKeyword.toLowerCase();
		const result = activeFiltered.filter((u) =>
			SEARCH_FIELDS.some((field) => {
				const val = u[field];
				return typeof val === "string" && val.toLowerCase().includes(kw);
			}),
		);
		debugLog(4, "[UserGroupsList][PROCESS] visibleGroups filter fields", {
			searchFields: SEARCH_FIELDS,
			searchKeyword: searchKeyword,
		});

		return result;
	}, [userGroupsList, showInactive, searchKeyword]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		debugLog(3, "[UserGroupsList][ACTION] handleSelectionChange", {
			count: newSelected?.length ?? 0,
		});
		setItemSelected(newSelected);
	};

	const headCells = [
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Group Name",
		},
		{
			id: "group_code",
			numeric: false,
			disablePadding: false,
			label: "Group Code",
		},
		{
			id: "member_desc",
			numeric: false,
			disablePadding: false,
			label: "Members",
			disableSort: true,
		},
		{
			id: "createdAt",
			numeric: false,
			disablePadding: false,
			label: "Date Created",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Updated",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Status",
		},
	];

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		selected,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visibleGroups, // Or from Redux selector
		initialOrderBy: "updatedAt",
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
	});

	// --- Handlers ---
	const rowClickHandler = useCallback(
		(userGroup) => {
			debugLog(3, "[UserGroupsList][ACTION] rowClickHandler", {
				id: userGroup?.id,
				name: userGroup?.name,
			});

			// It's dispatching setCurrentUser(user) without validating whether this user contains the most up-to-date data.
			// If a stale or partial user object is passed in, the EditUser screen might render outdated data.
			// dispatch(setCurrentUser(user));

			// Trigger a full initFetchOne() via id
			// to ensure the state always reflects the latest from backend.
			// In-app open path standard: resolve selected record by numeric id.
			onSelectUserGroup(userGroup.id);
		},
		[onSelectUserGroup],
	);

	/**
	 * Handle keyword change emitted from RecordNav search box
	 * @param {string} keyword - The current search term from RecordNav
	 */
	const handleSearchChange = (keyword) => {
		setSearchKeyword(keyword);
		debugLog(3, "[UserGroupsList][ACTION] handleSearchChange", { keyword: keyword });
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[UserGroupsList][ACTION] toggleInactiveHandler", { option: option });

		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[UserGroupsList][ACTION] sortByHandler", { option: option });

		setOrderBy(option.orderBy);
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[UserGroupsList][ACTION] changeRowsPerPageHandler", { option: option });

		setRowsPerPage(option.id);
		props.setRowsPerPage(option.id);
		setPage(0);
		setPageM1(1);
	};

	// User-group sorting should match the actual fields exposed in both the
	// card layout and the developer table layout.
	const sortByOptions = [
		{ disabled: false, id: 11, orderBy: "updatedAt", order: "asc", label: "Update Date (asc)" },
		{ disabled: false, id: 12, orderBy: "updatedAt", order: "desc", label: "Update Date (desc)" },
		{ disabled: false, id: 21, orderBy: "createdAt", order: "asc", label: "Create Date (asc)" },
		{ disabled: false, id: 22, orderBy: "createdAt", order: "desc", label: "Create Date (desc)" },
		{ disabled: false, id: 31, orderBy: "name", order: "asc", label: "Group Name (asc)" },
		{ disabled: false, id: 32, orderBy: "name", order: "desc", label: "Group Name (desc)" },
		{ disabled: false, id: 35, orderBy: "group_code", order: "asc", label: "Group Code (asc)" },
		{ disabled: false, id: 36, orderBy: "group_code", order: "desc", label: "Group Code (desc)" },
		{ disabled: false, id: 41, orderBy: "status_id", order: "asc", label: "Status (asc)" },
		{ disabled: false, id: 42, orderBy: "status_id", order: "desc", label: "Status (desc)" },
	];

	// Keep the developer-table date columns visually aligned when both
	// created and updated timestamps are shown side by side.
	const compactDateCellStyle = { fontSize: "0.7rem" };

	// Inside your component (replace the current <Paper>...<Table.Root>...</Paper> block):
	const columns = [
		{
			key: "name",
			title: "Group Name",
			minW: 180,
			render: (row) => {
				const isSystemGroup = row.id <= 0;
				return (
					<>
						{htmlParser.parse(highlight(searchKeyword, row.name, false))}
						{isSystemGroup && (
							<MetreaTooltip content="System group - managed by system">
								<Icon
									as={InfoRegular}
									boxSize="1.1rem"
									cursor="pointer"
									aria-label="System group"
									role="button"
									tabIndex={0}
									margin="6px"
									onClick={(e) => {
										e.stopPropagation(); // [CRITICAL] don't trigger rowClick
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.stopPropagation();
										}
									}}
								/>
							</MetreaTooltip>
						)}
					</>
				);
			},

			tooltip: (row) => {
				const hasComments = Boolean(row.comments?.trim().length > 0);
				return hasComments ? "always" : "auto";
			},
			tooltipText: (row) => {
				const hasComments = Boolean(row.comments?.trim().length > 0);
				if (!hasComments) return undefined;

				const tooltipLines = [
					row.name,
					"", // blank line for spacing
					"Note/Comments:",
					row.comments,
				];
				// Join with line breaks so EllipsisTooltip will display multiline correctly
				return tooltipLines.join("\n");
			},
		},
		{
			key: "group_code",
			title: "Group Code",
			minW: 100,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.group_code, false)),
		},
		{
			key: "member_desc",
			title: "Members",
			minW: 120,
			stackGrow: 2,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.member_desc || "-", false)),
		},
		{
			key: "createdAt",
			title: "Date Created",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateString(row.createdAt),
		},
		{
			key: "updatedAt",
			title: "Last Updated",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateTimeString(row.updatedAt),
		},
		{
			key: "status_id",
			title: "Status",
			minW: 60,
			tooltip: undefined, // or omit the field to disable the ellipsis tooltip path
			render: (row) => (row?.status_id === 1 ? "Active" : "Inactive"),
		},
	];

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[UserGroupsList][FLOW] render", {
		renderCount: renderCount.current,
		rowsPerPage: rowsPerPage,
		pageM1: pageM1,
		selectedCount: selected?.length ?? 0,
		visibleGroupCount: visibleGroups?.length ?? 0,
		showInactive: showInactive,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<>
			<Box w="100%">
				{/* Replace Table/Paper block with CardList */}
				<CardList
					rows={visibleRows}
					keyField="id"
					columns={columns}
					toolbarSlot={
						<RecordNav
							data={visibleGroups}
							showSearch={isDeveloper()}
							searchFields={SEARCH_FIELDS}
							columns={columns}
							onSearchChange={handleSearchChange}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={(opt) => sortByHandler({ orderBy: opt.orderBy, order: opt.order })}
							onChangeRowsPerPage={(opt) => changeRowsPerPageHandler(opt)}
							callFrom="Listing"
							sortByOptions={sortByOptions}
							orderBy={orderBy}
							order={order}
						/>
					}
					showLabels
					density="compact"
					responsive={{
						mode: "auto",
						maxRows: 5,
						minColWidth: 100,
						gap: 12,
					}}
					showVerticalDividers
					paginationSlot={
						<Pagination.Root
							count={visibleGroups.length}
							pageSize={Number(rowsPerPage) || 10}
							page={pageM1}
							onPageChange={({ page }) => handleChangePageM1(undefined, page)}
						>
							<ButtonGroup variant="ghost" size="2xs">
								<Pagination.PrevTrigger asChild>
									<IconButton>
										<ChevronLeftRegular />
									</IconButton>
								</Pagination.PrevTrigger>
								<Pagination.Items
									render={(page) => (
										<IconButton variant={{ base: "ghost", _selected: "solid" }}>
											{page.value}
										</IconButton>
									)}
								/>
								<Pagination.NextTrigger asChild>
									<IconButton>
										<ChevronRightRegular />
									</IconButton>
								</Pagination.NextTrigger>
							</ButtonGroup>
						</Pagination.Root>
					}
					rowActions={(row) => {
						const actions = [];
						actions.push({
							id: "open",
							label: "Open",
							disabled: row.id <= 0,
							onSelect: (row) => rowClickHandler(row),
						});

						return actions;
					}}
				/>
			</Box>

			{/* Keep the legacy table mirror behind testing mode while draining its
			    wrapper-backed controls onto direct v2 primitives. */}
			{showTesting() && isDeveloper() && (
				<>
					<Box marginTop="1200px"></Box>
					<Box w="100%">
						<RecordNav
							data={visibleGroups}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={sortByHandler}
							onChangeRowsPerPage={changeRowsPerPageHandler}
							sortByOptions={sortByOptions}
							orderBy={orderBy}
							order={order}
						/>
						<Box w="100%" mb={3} boxShadow="sm" bg="white" rounded="md">
							{showTesting() && isDevTeam() && (
								<EnhancedTableToolbar numSelected={selected.length} />
							)}
							<Box overflowX="auto">
								<Table.Root
									size={dense ? "sm" : "md"}
									variant="outline"
									aria-labelledby="tableTitle"
								>
									<EnhancedTableHead
										numSelected={selected.length}
										order={order}
										orderBy={orderBy}
										onSelectAllClick={handleSelectAllClick}
										onRequestSort={handleRequestSort}
										rowCount={visibleGroups.length}
										headCells={headCells}
										styles={styles}
									/>

									<Table.Body>
										{visibleRows.map((row, index) => {
											const isItemSelected = isSelected(row.id);
											const labelId = `enhanced-table-checkbox-${index}`;
											const isDisabled = row.id <= 0;

											return (
												<Table.Row
													key={row.id}
													data-selected={isItemSelected}
													className={styles.tableBodyRow}
													data-disabled={isDisabled}
													aria-disabled={isDisabled}
													style={{ cursor: isDisabled ? "default" : "pointer" }}
												>
													{showTesting() && isDevTeam() && (
														<Table.Cell>
															<Checkbox.Root
																checked={isItemSelected}
																disabled={isDisabled}
																onCheckedChange={
																	isDisabled
																		? undefined
																		: (next) =>
																				checkHandler(
																					{
																						target: {
																							checked:
																								next ===
																									true ||
																								next?.checked ===
																									true ||
																								next?.detail
																									?.checked ===
																									true,
																						},
																					},
																					row.id,
																				)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																}}
															>
																<Checkbox.HiddenInput
																	aria-labelledby={labelId}
																/>
																<Checkbox.Control />
															</Checkbox.Root>
														</Table.Cell>
													)}
													<Table.Cell
														onClick={
															isDisabled
																? undefined
																: () => rowClickHandler(row)
														}
													>
														{row.name}{" "}
														{isDisabled ? (
															<MetreaTooltip content="Managed by system.">
																<Text style={{ fontWeight: 600 }}> * </Text>
															</MetreaTooltip>
														) : (
															""
														)}
													</Table.Cell>
													<Table.Cell
														onClick={
															isDisabled
																? undefined
																: () => rowClickHandler(row)
														}
													>
														{row.group_code}
													</Table.Cell>
													<Table.Cell
														onClick={
															isDisabled
																? undefined
																: () => rowClickHandler(row)
														}
													>
														{row.member_desc || "-"}
													</Table.Cell>
													<Table.Cell
														onClick={
															isDisabled
																? undefined
																: () => rowClickHandler(row)
														}
														style={compactDateCellStyle}
													>
														{getShortDateString(row.createdAt)}
													</Table.Cell>
													<Table.Cell
														onClick={
															isDisabled
																? undefined
																: () => rowClickHandler(row)
														}
														style={compactDateCellStyle}
													>
														{getShortDateTimeString(row.updatedAt)}
													</Table.Cell>
													<Table.Cell
														onClick={
															isDisabled
																? undefined
																: () => rowClickHandler(row)
														}
													>
														{row.status_id === 1 ? "Active" : "Inactive"}
													</Table.Cell>
												</Table.Row>
											);
										})}
										{emptyRows > 0 && (
											<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
												<Table.Cell
													colSpan={
														(showTesting() && isDevTeam() ? 1 : 0) +
														headCells.length
													}
													style={{ borderBottom: "none", padding: 0 }}
												/>
											</Table.Row>
										)}
									</Table.Body>
								</Table.Root>
							</Box>
						</Box>

						<Box className="flex-auto justify-items-center p-1">
							<Pagination.Root
								count={visibleGroups.length}
								pageSize={Number(rowsPerPage) || 10}
								page={pageM1}
								onPageChange={({ page }) => handleChangePageM1(undefined, page)}
							>
								<ButtonGroup variant="ghost" size="2xs">
									<Pagination.PrevTrigger asChild>
										<IconButton>
											<ChevronLeftRegular />
										</IconButton>
									</Pagination.PrevTrigger>
									<Pagination.Items
										render={(page) => (
											<IconButton variant={{ base: "ghost", _selected: "solid" }}>
												{page.value}
											</IconButton>
										)}
									/>
									<Pagination.NextTrigger asChild>
										<IconButton>
											<ChevronRightRegular />
										</IconButton>
									</Pagination.NextTrigger>
								</ButtonGroup>
							</Pagination.Root>
						</Box>

						{showTesting() && isDevTeam() && isDevSiteNow() && (
							<Switch.Root
								size="sm"
								className="m-[10px]"
								checked={dense}
								onCheckedChange={(next) =>
									handleChangeDense({
										target: {
											checked:
												next === true ||
												next?.checked === true ||
												next?.detail?.checked === true,
										},
									})
								}
							>
								<Switch.HiddenInput />
								<Switch.Control>
									<Switch.Thumb />
								</Switch.Control>
								<Switch.Label>Dense padding</Switch.Label>
							</Switch.Root>
						)}
					</Box>
				</>
			)}
		</>
	);
};

export default UserGroupsList;
